import { mapActions, mapState } from '../../../../../../node_modules/pinia/dist/pinia.mjs?vue3';
import { GlLoadingIcon, GlIcon } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { debounce } from 'lodash-es';
import fuzzaldrinPlus from 'fuzzaldrin-plus';
import { s__ } from '~/locale';
import HighlightedText from '~/vue_shared/components/highlighted_text.vue';
import { joinPaths, buildURLwithRefType } from '~/lib/utils/url_utility';
import { projectPath as projectBasePath } from '~/lib/utils/path_helpers/project';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import axios from '../../../lib/utils/axios_utils.js?vue3';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY, ESC_KEY } from '~/lib/utils/keys';
import { FOCUS_FILE_TREE_BROWSER_FILTER_BAR, keysFor } from '~/behaviors/shortcuts/keybindings';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { useFileTreeBrowserVisibility } from '../../stores/file_tree_browser_visibility.js?vue3';
import { Mousetrap } from '~/lib/mousetrap';
import { InternalEvents } from '../../../tracking/index.js?vue3';
export default {
    name: 'FileTreeSearch',
    components: {
        GlLoadingIcon,
        GlIcon,
        HighlightedText
    },
    mixins: [
        InternalEvents.mixin()
    ],
    props: {
        projectPath: {
            type: String,
            required: true
        },
        refType: {
            type: String,
            required: false,
            default: ''
        },
        escapedRef: {
            type: String,
            required: true
        }
    },
    data () {
        return {
            searchQuery: '',
            searchResults: [],
            loadError: false,
            showSearchPanel: false,
            focusedIndex: -1,
            allFiles: [],
            filesLoaded: false,
            isLoading: false
        };
    },
    computed: {
        ...mapState(useFileTreeBrowserVisibility, [
            'fileTreeBrowserIsPeekOn'
        ]),
        filterSearchShortcutKey () {
            if (this.shortcutsDisabled) {
                return null;
            }
            return keysFor(FOCUS_FILE_TREE_BROWSER_FILTER_BAR)[0];
        },
        shortcutsDisabled () {
            return keyboardShortcutsDisabled();
        },
        hasResults () {
            return this.searchResults.length > 0;
        }
    },
    watch: {
        filesLoaded (loaded) {
            if (loaded && this.searchQuery) {
                this.searchFiles(this.searchQuery);
            }
        },
        searchResults () {
            this.focusedIndex = -1;
        }
    },
    mounted () {
        this.debouncedSearchFiles = debounce(this.searchFiles, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
        this.mousetrap = new Mousetrap();
        if (!this.shortcutsDisabled) {
            this.mousetrap.bind(keysFor(FOCUS_FILE_TREE_BROWSER_FILTER_BAR), this.focusSearchInput);
        }
    },
    beforeDestroy () {
        this.mousetrap.unbind(keysFor(FOCUS_FILE_TREE_BROWSER_FILTER_BAR));
    },
    methods: {
        ...mapActions(useFileTreeBrowserVisibility, [
            'resetFileTreeBrowserAllStates'
        ]),
        focusSearchInput () {
            this.trackEvent('focus_file_tree_browser_filter_bar_on_repository_page', {
                label: 'shortcut'
            });
            this.$refs.searchInput?.focus();
        },
        async loadAllFiles () {
            this.isLoading = true;
            try {
                const url = joinPaths(projectBasePath(this.projectPath), '-', 'files', this.escapedRef);
                const { data } = await axios.get(url);
                this.allFiles = data.map((filePath)=>{
                    const routerPath = buildURLwithRefType({
                        path: joinPaths('/', '-/blob', this.escapedRef, filePath.split('/').map(encodeURIComponent).join('/')),
                        refType: this.refType
                    });
                    return {
                        id: filePath,
                        path: filePath,
                        routerPath
                    };
                });
                this.filesLoaded = true;
                this.loadError = false;
            } catch (error) {
                this.filesLoaded = false;
                this.loadError = true;
            } finally{
                this.isLoading = false;
            }
        },
        onSearchInput (query) {
            this.searchQuery = query;
            this.showSearchPanel = Boolean(query);
            if (!query || !this.filesLoaded) {
                return;
            }
            this.debouncedSearchFiles(query);
        },
        async onSearchFocus () {
            this.trackEvent('focus_file_tree_browser_filter_bar_on_repository_page', {
                label: 'click'
            });
            if (!this.filesLoaded && !this.isLoading) {
                await this.loadAllFiles();
            }
        },
        searchFiles (query) {
            if (!query || query.length < 1) {
                this.searchResults = [];
                return;
            }
            const filteredItems = fuzzaldrinPlus.filter(this.allFiles, query, {
                key: 'path',
                maxResults: 20
            });
            this.searchResults = filteredItems;
        },
        handleResultClick (result) {
            this.$router.push(result.routerPath);
            this.searchQuery = '';
            this.showSearchPanel = false;
            if (this.fileTreeBrowserIsPeekOn) {
                this.resetFileTreeBrowserAllStates();
            }
        },
        handleKeydown (event) {
            const { key } = event;
            const items = this.getResultItems();
            if (key === ARROW_DOWN_KEY) {
                event.preventDefault();
                this.focusedIndex = Math.min(this.focusedIndex + 1, items.length - 1);
                this.focusItem();
            } else if (key === ARROW_UP_KEY) {
                event.preventDefault();
                this.focusedIndex = Math.max(this.focusedIndex - 1, -1);
                if (this.focusedIndex === -1) {
                    this.$refs.searchInput?.focus();
                } else {
                    this.focusItem();
                }
            } else if (key === ENTER_KEY) {
                event.preventDefault();
                if (this.focusedIndex >= 0 && items[this.focusedIndex]) {
                    items[this.focusedIndex].querySelector('button')?.click();
                }
            } else if (key === ESC_KEY) {
                event.preventDefault();
                this.clearSearch();
            }
        },
        getResultItems () {
            return Array.from(this.$refs.resultsList?.querySelectorAll('.file-tree-search-result-item') || []);
        },
        focusItem () {
            const items = this.getResultItems();
            if (items[this.focusedIndex]) {
                items[this.focusedIndex].focus();
            }
        },
        clearSearch () {
            this.searchQuery = '';
            this.showSearchPanel = false;
            this.$refs.searchInput?.focus();
        },
        handleInputKeydown (event) {
            if (!this.hasResults) return;
            event.preventDefault();
            this.focusedIndex = 0;
            this.focusItem();
        }
    },
    i18n: {
        searchLabel: s__('Repository|Search files (*.vue, *.rb...)'),
        noResults: s__('Repository|No results found'),
        errorMessage: s__('Repository|Something went wrong while loading the files')
    }
};
